import { useMemo } from 'react';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { lifeAreaOtherID } from 'shared/types/orderable-section';
import { SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';

export function filterEmptyChildrenCascading(
  data: Record<ID, TreeItem>,
): Record<ID, TreeItem> {
  // 1. Use structuredClone for a clean, deep copy. O(N)
  const tempRecord = structuredClone(data);
  const itemsToRemove = new Set<ID>();

  // 2. Build parent map and find initial leaves. O(N)
  const parentMap = new Map<ID, Set<ID>>(); // Maps childId -> Set<parentId>
  const leafQueue: ID[] = []; // This will act as our queue

  // Use Object.values() and forEach for a modern iteration
  Object.values(tempRecord).forEach((item) => {
    // Check if it's an initial leaf (and not 'task-1' which has no children prop)
    if (item.children && !item.children.length) {
      leafQueue.push(item.id);
      itemsToRemove.add(item.id);
    }

    // Populate the parentMap
    if (item.children) {
      item.children.forEach((childId) => {
        if (!parentMap.has(childId)) {
          parentMap.set(childId, new Set());
        }
        parentMap.get(childId)!.add(item.id);
      });
    }
  });

  // We use a `for` loop as a performant queue. We are *adding* to
  // leafQueue inside the loop, so it's a dynamic 'while' loop.
  // This avoids the O(N) cost of array.shift().
  for (let i = 0; i < leafQueue.length; i++) {
    const idToRemove = leafQueue[i]; // O(1) read
    const parentIds = parentMap.get(idToRemove);

    if (parentIds) {
      parentIds.forEach((parentId) => {
        // Only process parents we haven't already marked for removal
        if (!itemsToRemove.has(parentId)) {
          const parent = tempRecord[parentId];

          if (parent.children) {
            // Remove the child from the parent's list
            parent.children = parent.children.filter((c) => c !== idToRemove);

            // If the parent is now a leaf, add it to the queue.
            // The `for` loop will automatically pick it up.
            if (!parent.children.length) {
              leafQueue.push(parentId);
              itemsToRemove.add(parentId);
            }
          }
        }
      });
    }
  }

  // delete all items that have to be removed
  itemsToRemove.forEach((id) => {
    delete tempRecord[id];
  });

  return tempRecord;
}

const getGoalLifeArea = (goal: Goal, goals: Goal[]): ID | undefined => {
  if (goal.lifeAreaId || !goal.parentIds?.length) {
    return goal.lifeAreaId ?? undefined;
  }

  const parentGoal = goals.find((g) => g.id === goal.parentIds?.[0]);
  return parentGoal ? getGoalLifeArea(parentGoal, goals) : undefined;
};

export type Options = {
  lifeAreas: LifeArea[];
  goals: Goal[];
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
};

export const useLifeAreaTree = ({
  lifeAreas,
  goals,
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
}: Options) =>
  useMemo<Record<ID, TreeItem>>(() => {
    // create a base of treeItems with lifeArea tree items
    const lifeAreaTreeItems = lifeAreas.reduce<Record<ID, TreeItem>>(
      (acc, lifeArea) => {
        acc[lifeArea.id] = {
          id: lifeArea.id,
          type: TreeItemTypes.LifeArea,
          name: lifeArea.name,
          children: [],
          isFolder: true,
        };
        return acc;
      },
      {},
    );
    lifeAreaTreeItems[lifeAreaOtherID] = {
      id: lifeAreaOtherID,
      type: TreeItemTypes.LifeArea,
      name: 'Other',
      children: [],
      isFolder: true,
    };
    const customTaskOrder =
      sortingMode === SortingMode.Custom
        ? Object.keys(lifeAreaTreeItems).reduce<ID[]>((acc, lifeAreaId) => {
            const lifeAreaOrder = customOrder?.[lifeAreaId] ?? [];
            acc.push(...lifeAreaOrder);
            return acc;
          }, [])
        : [];

    // process tasks to tree items to finish it off with
    const allTreeItems = getSortedTasks(tasks, {
      today,
      weekStartsOn,
      order: customTaskOrder,
      mode: sortingMode ? [SortingMode.CreatedAt, sortingMode] : undefined,
    }).reduce<Record<ID, TreeItem>>((acc, task) => {
      const parentGoal = task.goalId
        ? goals.find((g) => g.id === task.goalId)
        : undefined;
      const parentLifeAreaId =
        (parentGoal ? getGoalLifeArea(parentGoal, goals) : task.lifeAreaId) ??
        lifeAreaOtherID;
      const parentTreeItem = acc[parentLifeAreaId];
      if (parentTreeItem) {
        parentTreeItem.children?.push(task.id);
        acc[task.id] = {
          id: task.id,
          name: task.name,
          type: TreeItemTypes.Task,
        };
      }
      return acc;
    }, lifeAreaTreeItems);

    const filteredTreeItems = filterEmptyChildrenCascading(allTreeItems);
    const topLevelItemIds = Object.keys(filteredTreeItems).filter(
      (id) => filteredTreeItems[id].type === TreeItemTypes.LifeArea,
    );

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TreeItemTypes.Root,
        children: topLevelItemIds,
      },
      ...filteredTreeItems,
    };
  }, [customOrder, goals, lifeAreas, sortingMode, tasks, today, weekStartsOn]);
